import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../utils/userContext";
import axios from "../../utils/axiosInstance";
import Button from "../ui/Button";
import SortableList, { DragHandle } from "../ui/SortableList";
import AttachFromSetModal from "./AttachFromSetModal";
import AddTaskFromRegistryModal from "./AddTaskFromRegistryModal";
import TaskBuilderModal from "./TaskBuilderModal";
import TaskCard from "./TaskCard";

/**
 * Main section for displaying and managing project tasks
 * Shows bundled tasks, progress, and provides actions to add tasks
 * 
 * @param {string} projectId - The project ID
 * @param {boolean} readOnly - If true, hides all management actions (view mode)
 * @param {boolean} compact - If true, shows a minimal UI without progress bars (for edit mode)
 * @param {function} onBundleChange - Callback when bundle changes (for edit mode)
 */
const ProjectTasksSection = ({ projectId, readOnly = false, compact = false, onBundleChange }) => {
  const { user } = useContext(UserContext);
  const canManage = user?.isAdmin && !readOnly;
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Modal states
  const [showAttachSetModal, setShowAttachSetModal] = useState(false);
  const [showAddFromRegistryModal, setShowAddFromRegistryModal] = useState(false);
  const [showAddAdHocModal, setShowAddAdHocModal] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchBundle();
    }
  }, [projectId]);

  const fetchBundle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/task-bundles/projects/${projectId}/task-bundle`, {
        params: compact ? { skipTranslations: true } : undefined,
      });
      setBundle(res.data);
      
      // Fetch summary stats (skip in compact mode for performance)
      if (!compact) {
        const summaryRes = await axios.get(`/task-bundles/${res.data._id}/summary`);
        setSummary(summaryRes.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setBundle(null);
        setSummary(null);
      } else {
        console.error("Error fetching bundle:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnsureBundle = async () => {
    try {
      const res = await axios.post(`/task-bundles/projects/${projectId}/task-bundles`);
      setBundle(res.data);
      return res.data;
    } catch (error) {
      console.error("Error creating bundle:", error);
      alert("Failed to initialize task bundle");
      return null;
    }
  };

  const handleBundleCreated = () => {
    setShowAttachSetModal(false);
    setShowAddFromRegistryModal(false);
    setShowAddAdHocModal(false);
    setShowDropdown(false);
    fetchBundle();
    // Notify parent of bundle change
    if (onBundleChange) onBundleChange();
  };

  const handleDropdownOption = (action) => {
    setShowDropdown(false);
    action();
  };

  const handleReorderTasks = async (reorderedItems) => {
    if (!canManage || !bundle) return;
    
    // Update local state optimistically with the new order
    const newTasks = reorderedItems.map(item => item.task);
    setBundle({ ...bundle, tasks: newTasks });
    
    try {
      const taskIds = reorderedItems.map(item => item.id);
      await axios.put(`/task-bundles/${bundle._id}/reorder-tasks`, { taskIds });
    } catch (error) {
      console.error("Error reordering tasks:", error);
      alert("Failed to reorder tasks");
      // Revert by refetching on error
      fetchBundle();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.task-dropdown-container')) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  if (loading) {
    return (
      <div>
        <p className="text-gray-500">Loading tasks...</p>
      </div>
    );
  }

  const taskModals = (
    <>
      {showAttachSetModal && (
        <AttachFromSetModal
          projectId={projectId}
          onClose={() => setShowAttachSetModal(false)}
          onSuccess={handleBundleCreated}
        />
      )}

      {showAddFromRegistryModal && (
        <AddTaskFromRegistryModal
          bundleId={bundle?._id}
          projectId={projectId}
          onClose={() => setShowAddFromRegistryModal(false)}
          onSuccess={handleBundleCreated}
        />
      )}

      {showAddAdHocModal && (
        <TaskBuilderModal
          bundleId={bundle?._id}
          projectId={projectId}
          isOpen={showAddAdHocModal}
          onClose={() => setShowAddAdHocModal(false)}
          onSuccess={handleBundleCreated}
        />
      )}
    </>
  );

  if (!bundle) {
    return (
      <>
        <div>
          <h3 className={compact ? "text-sm font-semibold text-gray-700 mb-2" : "text-lg font-bold mb-4"}>Tasks</h3>
          <p className={compact ? "text-gray-500 text-sm mb-3" : "text-gray-600 mb-4"}>
            No tasks have been added to this project yet.
          </p>
          {canManage && (
            <div className={compact ? "flex flex-wrap gap-2" : "space-y-2"}>
              <Button
                variant="solid"
                size={compact ? "sm" : "md"}
                onClick={() => {
                  handleEnsureBundle();
                  setShowAttachSetModal(true);
                }}
                className={compact ? "" : "w-full"}
              >
                Attach Task Set
              </Button>
              <Button
                variant="outline"
                size={compact ? "sm" : "md"}
                onClick={() => {
                  handleEnsureBundle();
                  setShowAddFromRegistryModal(true);
                }}
                className={compact ? "" : "w-full"}
              >
                Add Task
              </Button>
              <Button
                variant="outline"
                size={compact ? "sm" : "md"}
                onClick={async () => {
                  await handleEnsureBundle();
                  setShowAddAdHocModal(true);
                }}
                className={compact ? "" : "w-full"}
              >
                Create Custom
              </Button>
            </div>
          )}
        </div>
        {taskModals}
      </>
    );
  }

  return (
    <>
      <div>
        {/* Header */}
        <div className={`flex justify-between items-center ${compact ? 'mb-2' : 'mb-4'}`}>
          <div className="flex items-center gap-2">
            <h3 className={compact ? "text-sm font-semibold text-gray-700" : "text-lg font-bold"}>Tasks</h3>
            {compact && bundle?.tasks && (
              <span className="text-xs text-gray-500">
                ({bundle.tasks.length} {bundle.tasks.length === 1 ? 'task' : 'tasks'})
              </span>
            )}
          </div>
          {canManage && (
            <div className="relative task-dropdown-container">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Task options"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => handleDropdownOption(() => setShowAttachSetModal(true))}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Attach task set
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDropdownOption(() => setShowAddFromRegistryModal(true))}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Attach task
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDropdownOption(() => setShowAddAdHocModal(true))}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Add new task
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress Summary - hidden in compact mode */}
        {!compact && summary && (
          <div className="space-y-3">
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${summary.progressPercent}%` }}
                />
              </div>
            </div>
            
            <div className="flex justify-between items-center text-sm">
              <div>
                <span className="font-medium text-gray-800">{summary.totalTasks}</span>
                <span className="text-gray-500 ml-1">ToDo</span>
              </div>
              <div>
                <span className="font-medium text-blue-600">{summary.inProgressSubtasks}</span>
                <span className="text-gray-500 ml-1">In Progress</span>
              </div>
              <div>
                <span className="font-medium text-green-600">{summary.completedSubtasks}</span>
                <span className="text-gray-500 ml-1">Done</span>
              </div>
              <div>
                <span className="font-medium text-red-600">{summary.blockedSubtasks}</span>
                <span className="text-gray-500 ml-1">Blocked</span>
              </div>
            </div>
          </div>
        )}

        {/* Task List */}
        {bundle?.tasks && bundle.tasks.length > 0 && (
          <div className={compact ? "mt-2" : "mt-4"}>
            {canManage ? (
              <SortableList
                items={bundle.tasks.map((task, idx) => ({
                  id: task._id,
                  task,
                  taskIdx: idx
                }))}
                onReorder={handleReorderTasks}
                renderItem={(item, dragHandleProps) => (
                  <TaskCard
                    task={item.task}
                    taskIdx={item.taskIdx}
                    bundleId={bundle._id}
                    onUpdate={fetchBundle}
                    dragHandleProps={dragHandleProps}
                    readOnly={readOnly}
                    compact={compact}
                  />
                )}
              />
            ) : (
              <div className={compact ? "space-y-1" : "space-y-3"}>
                {bundle.tasks.map((task, taskIdx) => (
                  <TaskCard
                    key={taskIdx}
                    task={task}
                    taskIdx={taskIdx}
                    bundleId={bundle._id}
                    onUpdate={fetchBundle}
                    readOnly={readOnly}
                    compact={compact}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {taskModals}
    </>
  );
};

export default ProjectTasksSection;
